import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { usePatchObjectiveMutation } from 'services/objectives/api';
import { Objective, ObjectiveTarget } from 'services/objectives/types';
import styles from './ObjectiveProgress.module.css';

interface DraftPoint {
  at: string;
  key: string;
  value: string;
}

interface DraftTarget {
  id: string;
  name: string;
  points: DraftPoint[];
}

interface ObjectiveTargetsProps {
  objective: Objective;
}

const newId = () => (
  globalThis.crypto?.randomUUID?.()
  ?? `target-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const toDraft = (targets?: ObjectiveTarget[] | null): DraftTarget[] => (
  (targets ?? []).map(target => ({
    id: target.id,
    name: target.name,
    points: target.points.map((point, index) => ({
      at: DateTime.fromISO(point.at).isValid
        ? DateTime.fromISO(point.at).toFormat('yyyy-MM-dd')
        : '',
      key: `${target.id}-${index}`,
      value: String(point.value),
    })),
  }))
);

const draftSignature = (draft: DraftTarget[]) => JSON.stringify(draft.map(target => ({
  id: target.id,
  name: target.name,
  points: target.points.map(point => ({ at: point.at, value: point.value })),
})));

const ObjectiveTargets = ({
  objective,
}: ObjectiveTargetsProps) => {
  const { t } = useTranslation();
  const [patchObjective] = usePatchObjectiveMutation();
  const [draft, setDraft] = useState<DraftTarget[]>(() => toDraft(objective.targets));
  const [error, setError] = useState<string | null>(null);
  const savedSignature = draftSignature(toDraft(objective.targets));

  useEffect(() => {
    setDraft(toDraft(objective.targets));
    setError(null);
  }, [objective.id, savedSignature]);

  const updateTarget = (id: string, update: (target: DraftTarget) => DraftTarget) => {
    setDraft(current => current.map(target => (target.id === id ? update(target) : target)));
  };

  const addTarget = () => {
    setDraft(current => [
      ...current,
      {
        id: newId(),
        name: '',
        points: [{ at: '', key: newId(), value: '' }],
      },
    ]);
  };

  const removeTarget = (id: string) => {
    setDraft(current => current.filter(target => target.id !== id));
  };

  const addPoint = (id: string) => {
    updateTarget(id, target => ({
      ...target,
      points: [...target.points, { at: '', key: newId(), value: '' }],
    }));
  };

  const removePoint = (targetId: string, pointKey: string) => {
    setDraft(current => current.flatMap(target => {
      if (target.id !== targetId) {
        return [target];
      }
      const points = target.points.filter(point => point.key !== pointKey);
      return points.length === 0 ? [] : [{ ...target, points }];
    }));
  };

  const onSave = async () => {
    const targets: ObjectiveTarget[] = [];
    const seenDates = new Set<string>();
    for (const target of draft) {
      const name = target.name.trim();
      if (!name || target.points.length === 0) {
        setError(t('required'));
        return;
      }
      const points: ObjectiveTarget['points'] = [];
      seenDates.clear();
      for (const point of target.points) {
        const parsed = Number(point.value);
        const at = DateTime.fromISO(point.at);
        if (point.value.trim() === '' || Number.isNaN(parsed) || !at.isValid) {
          setError(t('required'));
          return;
        }
        const day = at.toFormat('yyyy-MM-dd');
        if (seenDates.has(day)) {
          setError(t('required'));
          return;
        }
        seenDates.add(day);
        const iso = at.startOf('day').toISO();
        if (!iso) {
          setError(t('required'));
          return;
        }
        points.push({ at: iso, value: parsed });
      }
      points.sort((a, b) => a.at.localeCompare(b.at));
      targets.push({ id: target.id, name, points });
    }

    const result = await patchObjective({ id: objective.id, targets });
    if ('error' in result && result.error) {
      setError(t('invalidSubmission'));
      return;
    }
    setError(null);
  };

  return (
    <section aria-label={t('targets')} className={styles.targets}>
      <p><b>{t('targets')}</b></p>
      {error && <Alert variant="danger">{error}</Alert>}
      {
        draft.map(target => (
          <div className={styles.target} key={target.id}>
            <div className={styles.targetHeader}>
              <div>
                <label className="form-label" htmlFor={`target-name-${target.id}`}>{t('targetName')}</label>
                <input
                  className="form-control"
                  id={`target-name-${target.id}`}
                  onChange={event => {
                    const name = event.target.value;
                    updateTarget(target.id, current => ({ ...current, name }));
                  }}
                  value={target.name}
                />
              </div>
              <Button
                onClick={() => removeTarget(target.id)}
                size="sm"
                type="button"
                variant="outline-danger"
              >
                {t('delete')}
              </Button>
            </div>
            {
              target.points.map(point => (
                <div className={styles.pointRow} key={point.key}>
                  <div>
                    <label className="form-label" htmlFor={`target-date-${point.key}`}>{t('date')}</label>
                    <input
                      className="form-control"
                      id={`target-date-${point.key}`}
                      onChange={event => {
                        const at = event.target.value;
                        updateTarget(target.id, current => ({
                          ...current,
                          points: current.points.map(item => (
                            item.key === point.key ? { ...item, at } : item
                          )),
                        }));
                      }}
                      type="date"
                      value={point.at}
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor={`target-value-${point.key}`}>{t('value')}</label>
                    <input
                      aria-label={t('targetPointValue')}
                      className="form-control"
                      id={`target-value-${point.key}`}
                      onChange={event => {
                        const value = event.target.value;
                        updateTarget(target.id, current => ({
                          ...current,
                          points: current.points.map(item => (
                            item.key === point.key ? { ...item, value } : item
                          )),
                        }));
                      }}
                      step="any"
                      style={{ width: 120 }}
                      type="number"
                      value={point.value}
                    />
                  </div>
                  <Button
                    onClick={() => removePoint(target.id, point.key)}
                    size="sm"
                    type="button"
                    variant="outline-danger"
                  >
                    {t('delete')}
                  </Button>
                </div>
              ))
            }
            <div className={styles.targetActions}>
              <Button
                onClick={() => addPoint(target.id)}
                size="sm"
                type="button"
                variant="outline-secondary"
              >
                {t('addTargetPoint')}
              </Button>
            </div>
          </div>
        ))
      }
      <div className={styles.targetActions}>
        <Button
          onClick={addTarget}
          size="sm"
          type="button"
          variant="outline-secondary"
        >
          {t('addTarget')}
        </Button>
        <Button
          disabled={draftSignature(draft) === savedSignature}
          onClick={onSave}
          size="sm"
          type="button"
          variant="success"
        >
          {t('saveTargets')}
        </Button>
      </div>
    </section>
  );
};

export default ObjectiveTargets;
